import { useState, useRef } from "react";
import { Search, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useServices } from "@/lib/queries";
import { fmtINRInput } from "@emr/shared/india";
import { useDropdownNav } from "@/hooks/useDropdownNav";

export interface SelectedLineItem {
  serviceId: string | null;
  name: string;
  quantity: number;
  amountPaise: number;
}

interface Props {
  onAdd: (item: SelectedLineItem) => void;
}

export function ServicePicker({ onAdd }: Props) {
  const [q, setQ] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useServices();
  const services = (data && "rows" in data ? data.rows : Array.isArray(data) ? data : []) as { id: string; name: string; defaultAmountPaise: number; category: string | null }[];

  const filtered = q.trim().length === 0
    ? services
    : services.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()) || (s.category ?? "").toLowerCase().includes(q.toLowerCase()));

  const serviceResults = filtered.slice(0, 30);
  const { highlight: svcHighlight, highlightRef: svcHighlightRef, onKeyDown: onSvcSearchKeyDown } = useDropdownNav({
    count: !isLoading ? serviceResults.length : 0,
    onSelect: (i) => { if (serviceResults[i]) onAdd({ serviceId: serviceResults[i].id, name: serviceResults[i].name, quantity: 1, amountPaise: serviceResults[i].defaultAmountPaise }); },
    inputRef: searchRef,
  });

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <Input
          ref={searchRef}
          placeholder="Search services…"
          className="pl-9"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onSvcSearchKeyDown}
        />
      </div>
      <div className="visible-scrollbar max-h-48 overscroll-contain overflow-y-auto rounded-md border border-cream-200 bg-cream-100 dark:border-dark-700">
        {isLoading ? (
          <div className="p-2"><Skeleton className="h-6" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-3 text-center text-xs text-muted">No services found.</div>
        ) : (
          serviceResults.map((s, idx) => (
            <button
              key={s.id}
              ref={idx === svcHighlight ? svcHighlightRef : undefined}
              className={`flex w-full items-center justify-between gap-2 border-b border-cream-200 px-3 py-2 text-left text-sm hover:bg-cream-200 dark:border-dark-700 dark:hover:bg-dark-700 ${
                idx === svcHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
              }`}
              onClick={() => onAdd({ serviceId: s.id, name: s.name, quantity: 1, amountPaise: s.defaultAmountPaise })}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-ink dark:text-cream-50">{s.name}</div>
                {s.category && <div className="text-xs text-muted">{s.category}</div>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted">{fmtINRInput(s.defaultAmountPaise)}</span>
                <Plus size={14} className="text-maroon-600" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
